"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ShieldCheck, Zap, Eye } from "lucide-react";

const benefits = [
    {
        icon: ShieldCheck,
        title: "Protect Your Reputation",
        description: "Every email is verified before sending. Syntax, MX records, SMTP, and disposable detection — unsafe emails are automatically skipped to keep your sender score pristine.",
        color: "from-emerald-500 to-teal-600",
        bgGlow: "bg-emerald-500/10",
    },
    {
        icon: Zap,
        title: "Send Smarter",
        description: "Intelligent batch processing with rate limiting, backoff strategies, and multi-provider failover. Your emails hit inboxes, not spam folders.",
        color: "from-amber-500 to-orange-600",
        bgGlow: "bg-amber-500/10",
    },
    {
        icon: Eye,
        title: "See Everything",
        description: "Real-time WebSocket updates during sends. Full funnel tracking from delivery to open to click to unsubscribe — zero blind spots.",
        color: "from-indigo-500 to-violet-600",
        bgGlow: "bg-indigo-500/10",
    },
];

export default function BenefitsSection() {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section ref={ref} className="relative py-24 px-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold uppercase tracking-wider mb-4">
                        Why iComm
                    </span>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white tracking-tight">
                        Built for Teams That{" "}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-emerald-400 dark:to-teal-400">
                            Care About Deliverability
                        </span>
                    </h2>
                </motion.div>

                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {benefits.map((b, i) => (
                        <motion.div
                            key={b.title}
                            initial={{ opacity: 0, y: 30 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: 0.2 + i * 0.15 }}
                            className="group relative p-8 rounded-3xl bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl border border-gray-100 dark:border-white/[0.06] hover:shadow-2xl transition-all duration-500 overflow-hidden"
                        >
                            {/* Icon */}
                            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${b.color} flex items-center justify-center mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                                <b.icon className="h-7 w-7 text-white" />
                            </div>

                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
                                {b.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                {b.description}
                            </p>

                            {/* Background glow */}
                            <div className={`absolute -bottom-12 -right-12 w-40 h-40 rounded-full ${b.bgGlow} blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
